import {
    Box,
    Grid,
    Paper,
    Typography,
    useTheme,
    alpha,
    keyframes,
    Skeleton
} from '@mui/material';
import React from 'react';

// Subtle pulse animation
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Hover animation
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

export interface StatsCardProps {
    icon: React.ReactNode;
    value: string | number;
    label: string;
    color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
    trend?: number;
    subtitle?: string;
    loading?: boolean;
    title?: string;
}

const StatsCard: React.FC<StatsCardProps> = ({
    icon,
    value,
    label,
    color = 'primary',
    trend,
    subtitle,
    title,
    loading
}) => {
    const theme = useTheme();

    return (
        <Grid item xs={12} sm={6} md={3}>
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                }}
            >
                {loading ? (
                    <>
                        {/* Skeleton for icon */}
                        <Skeleton variant="circular" width={50} height={50} sx={{ mb: 2 }} />

                        {/* Skeleton for value */}
                        <Skeleton variant="text" width="60%" height={40} />

                        {/* Skeleton for label */}
                        <Skeleton variant="text" width="50%" height={24} />

                        {/* Skeleton for subtitle */}
                        <Skeleton variant="text" width="40%" height={18} />
                    </>
                ) : (
                    <>
                        {title && (
                            <Typography
                                variant="subtitle2"
                                color="text.primary"
                                sx={{ mb: 1 }}
                            >
                                {title}
                            </Typography>
                        )}

                        {/* Icon */}
                        <Box
                            className="icon-container"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 50,
                                height: 50,
                                borderRadius: 3,
                                backgroundColor: alpha(theme.palette[color].main, 0.1),
                                color: theme.palette[color].main,
                                mb: 2,
                                transition: 'all 0.3s ease',
                            }}
                        >
                            {icon}
                        </Box>

                        {/* Value */}
                        <Typography
                            variant="h4"
                            fontWeight="700"
                            color={theme.palette[color].dark}
                            sx={{
                                mb: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            {value}
                            {typeof trend === 'number' && trend !== 0 && (
                                <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{
                                        color: trend > 0 ? 'success.main' : 'error.main',
                                        fontWeight: 600,
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {trend > 0 ? '↗' : '↘'} {Math.abs(trend)}%
                                </Typography>
                            )}
                        </Typography>

                        {/* Label */}
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                        >
                            {label}
                        </Typography>

                        {/* Subtitle */}
                        {subtitle && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    fontStyle: 'italic',
                                    opacity: 0.8
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </>
                )}

                {/* Decorative element */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: alpha(theme.palette[color].main, 0.05),
                        zIndex: 0,
                    }}
                />
            </Paper>
        </Grid>
    );
};

export default StatsCard;